import {
  closestCorners,
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import React, { useMemo } from "react";
import Column from "../molecules/Column";
import TaskCard from "./TaskCard";

/**
 * KanbanBoard - Draggable kanban board component
 *
 * @param {Object} props
 * @param {Array} props.columns - Column definitions
 * @param {Array} props.tasks - Task data
 * @param {Array} props.users - User data
 * @param {Object} props.activeTask - Currently active (dragging) task
 * @param {Function} props.onDragStart - Drag start handler
 * @param {Function} props.onDragEnd - Drag end handler
 * @param {Function} props.onDragCancel - Drag cancel handler
 * @param {boolean} props.showArchived - Whether to show archived tasks
 * @param {Object} props.sensors - DnD sensors
 */
const KanbanBoard = ({
  columns,
  tasks,
  users,
  activeTask,
  onDragStart,
  onDragEnd,
  onDragCancel,
  showArchived = false,
  sensors,
}) => {
  // Memoize columns with filtered tasks for performance
  const columnsWithTasks = useMemo(() => {
    return columns
      .filter((column) => showArchived || column.status !== "Archived")
      .map((column) => ({
        ...column,
        tasks: tasks.filter((task) => task.status === column.status),
        count: tasks.filter((task) => task.status === column.status).length,
      }));
  }, [columns, tasks, showArchived]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={(args) => {
        // Try multiple collision detection algorithms in order of precision
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) return pointerCollisions;

        const rectCollisions = rectIntersection(args);
        if (rectCollisions.length > 0) return rectCollisions;

        // Fall back to closest corners for better UX
        return closestCorners(args);
      }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className="flex space-x-4 pb-4 overflow-x-auto min-h-[calc(100vh-180px)]">
        {columnsWithTasks.map((column) => (
          <Column
            key={column.id}
            id={column.id}
            title={column.title}
            status={column.status}
            color={column.color}
            count={column.count}
            tasks={tasks}
            users={users}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask ? <TaskCard task={activeTask} users={users} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
